export const formatDate = (date: Date): string => {
    return date.toLocaleDateString('en-US');
};

export const capitalizeFirstLetter = (string: string): string => {
    return string.charAt(0).toUpperCase() + string.slice(1);
};

export const isEmpty = (value: any): boolean => {
    return value === null || value === undefined || value === '';
};

/**
 * Format an epoch (ms) into a readable timestamp and relative minutes/hours ago.
 * Returns an em dash if the input is missing or invalid.
 */
export const formatEpoch = (epochMs?: number | string | null): string => {
    if (epochMs === null || epochMs === undefined || epochMs === '') return '\u2014';
    const n = typeof epochMs === 'string' ? parseInt(epochMs, 10) : epochMs;
    if (!n || Number.isNaN(n)) return '\u2014';
    const d = new Date(n);
    if (isNaN(d.getTime())) return '\u2014';
    // Show local timestamp with short timezone name (e.g. GMT+5, IST) and include IANA zone
    const formatted = d.toLocaleString(undefined, {
        year: 'numeric', month: 'numeric', day: 'numeric',
        hour: 'numeric', minute: 'numeric', second: 'numeric',
        hour12: true,
        timeZoneName: 'short'
    });
    const iana = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
    const diffMin = Math.floor((Date.now() - n) / 60000);
    let rel = '';
    if (diffMin <= 0) {
        rel = 'just now';
    } else if (diffMin < 60) {
        rel = `${diffMin} mins ago`;
    } else if (diffMin < 60 * 24) {
        const hrs = Math.floor(diffMin / 60);
        rel = `${hrs} hrs ago`;
    } else {
        const days = Math.floor(diffMin / (60 * 24));
        rel = `${days} days ago`;
    }
    return `${formatted}${iana ? ' [' + iana + ']' : ''} (${rel})`;
};

/**
 * Format an epoch (ms) into an ISO UTC timestamp string (e.g. 2025-09-15T13:45:30.000Z).
 * Returns an em dash if the input is missing or invalid.
 */
export const formatEpochUTC = (epochMs?: number | string | null): string => {
    if (epochMs === null || epochMs === undefined || epochMs === '') return '\u2014';
    const n = typeof epochMs === 'string' ? parseInt(epochMs, 10) : epochMs;
    if (!n || Number.isNaN(n)) return '\u2014';
    const d = new Date(n);
    if (isNaN(d.getTime())) return '\u2014';
    // Trim milliseconds to produce YYYY-MM-DDTHH:mm:ssZ
    return d.toISOString().replace(/\.\d{3}Z$/, 'Z');
};

/**
 * Format an epoch (ms) into local time string in MM-dd-yyyy HH:mm:ss
 * Returns an em dash if the input is missing or invalid.
 */
export const formatEpochLocal = (epochMs?: number | string | null): string => {
    if (epochMs === null || epochMs === undefined || epochMs === '') return '\u2014';
    const n = typeof epochMs === 'string' ? parseInt(epochMs, 10) : epochMs;
    if (!n || Number.isNaN(n)) return '\u2014';
    const d = new Date(n);
    if (isNaN(d.getTime())) return '\u2014';
    const pad = (v: number) => String(v).padStart(2, '0');
    const mm = pad(d.getMonth() + 1);
    const dd = pad(d.getDate());
    const yyyy = d.getFullYear();
    const HH = pad(d.getHours());
    const MM = pad(d.getMinutes());
    const ss = pad(d.getSeconds());
    return `${mm}-${dd}-${yyyy} ${HH}:${MM}:${ss}`;
};